import type { PageGuide } from '../../components/guide/guideTypes'

/**
 * Hand-crafted deep walkthroughs that override generated guides
 * for the most-used operational screens.
 */
export const curatedDeepGuides: PageGuide[] = [
  {
    match: ['/dashboard', '/'],
    title: { ar: 'شرح تفصيلي: لوحة التحكم', en: 'Detailed guide: Dashboard', fr: 'Guide: Tableau de bord' },
    summary: {
      ar: 'لوحة التحكم هي نقطة الانطلاق اليومية: مؤشرات، تنبيهات، وانتقال سريع لشاشات العمل.',
      en: 'The dashboard is your daily start: KPIs, alerts, and quick jumps to work screens.',
      fr: 'Point de départ quotidien : KPI, alertes et raccourcis.',
    },
    emoji: '🏠',
    accent: '#0d9488',
    gallery: [
      {
        src: '/help/dashboard/overview.svg',
        motif: 'dashboard',
        caption: { ar: 'نظرة عامة', en: 'Overview', fr: 'Vue d’ensemble' },
        highlight: { ar: 'اقرأ المؤشرات ثم انتقل للعمل', en: 'Read KPIs then jump to work', fr: 'Lire puis agir' },
      },
    ],
    sections: [
      {
        title: { ar: '1) قراءة المؤشرات', en: '1) Read KPIs', fr: '1) KPI' },
        steps: [
          {
            title: { ar: 'البطاقات العلوية', en: 'Top cards', fr: 'Cartes' },
            body: {
              ar: 'تعرض المبيعات، المشتريات، المستحقات، والتنبيهات حسب الفترة المختارة. قارن الأرقام مع يوم أمس لرصد الانحراف مبكراً.',
              en: 'Shows sales, purchases, receivables, and alerts for the selected period. Compare with yesterday to spot issues early.',
              fr: 'Ventes, achats, créances et alertes. Comparez avec la veille.',
            },
            shot: { src: '/help/dashboard/kpis.svg', motif: 'dashboard', caption: { ar: 'بطاقات KPI', en: 'KPI cards', fr: 'KPI' } },
          },
          {
            title: { ar: 'الرسوم والودجات', en: 'Charts & widgets', fr: 'Graphiques' },
            body: {
              ar: 'كل ودجت يلخّص مجالاً (مبيعات، مخزون، تحصيل). يمكنك إضافة أو إخفاء ودجات لتناسب دورك (محاسب / كاشير / مدير).',
              en: 'Each widget summarizes an area (sales, stock, collections). Add/hide widgets for your role.',
              fr: 'Chaque widget résume un domaine. Personnalisez selon le rôle.',
            },
            shot: { motif: 'settings', caption: { ar: 'تخصيص الودجات', en: 'Customize widgets', fr: 'Widgets' } },
          },
        ],
      },
      {
        title: { ar: '2) العمل السريع', en: '2) Quick work', fr: '2) Actions rapides' },
        steps: [
          {
            title: { ar: 'القائمة وCtrl+K', en: 'Sidebar & Ctrl+K', fr: 'Menu et Ctrl+K' },
            body: {
              ar: 'من الشريط الجانبي افتح القسم المطلوب، أو اضغط Ctrl+K واكتب اسم الشاشة (مثل فاتورة شراء) للانتقال فوراً.',
              en: 'Open modules from the sidebar, or press Ctrl+K and type a screen name (e.g. purchase invoice).',
              fr: 'Menu latéral ou Ctrl+K pour ouvrir un écran.',
            },
            shot: { motif: 'search', caption: { ar: 'بحث ذكي', en: 'Smart search', fr: 'Recherche' } },
          },
        ],
      },
    ],
    tips: [
      { ar: 'ابدأ يومك بمراجعة التنبيهات ثم فواتير اليوم.', en: 'Start the day with alerts, then today’s invoices.', fr: 'Alertes puis factures du jour.' },
    ],
  },
  {
    match: ['/invoices/new', '/invoices/:id/edit', '/invoices/:id/copy'],
    title: { ar: 'شرح تفصيلي: فاتورة المبيعات', en: 'Detailed guide: Sales invoice', fr: 'Guide: Facture vente' },
    summary: {
      ar: 'من اختيار العميل حتى التحصيل والطباعة — خطوات مرتبة لضمان خصم المخزون وترحيل الحسابات بشكل صحيح.',
      en: 'From customer selection to collection and print — ordered steps so stock and accounts post correctly.',
      fr: 'Du client à l’encaissement — stock et compta corrects.',
    },
    emoji: '🧾',
    accent: '#b45309',
    gallery: [
      {
        src: '/help/invoices/new.svg',
        motif: 'form',
        caption: { ar: 'رأس الفاتورة', en: 'Invoice header', fr: 'En-tête' },
      },
    ],
    sections: [
      {
        title: { ar: 'الخطوة أ: الرأس', en: 'Step A: Header', fr: 'Étape A' },
        steps: [
          {
            title: { ar: 'عميل + مخزن + تاريخ', en: 'Customer + store + date', fr: 'Client + dépôt + date' },
            body: {
              ar: 'اختر العميل (أو نقدي). حدّد المخزن الذي ستُصرف منه البضاعة والتاريخ الضريبي/التشغيلي. هذه الثلاثة أهم حقول قبل البنود.',
              en: 'Pick customer (or cash), the issuing store, and date. These three matter most before lines.',
              fr: 'Client, dépôt et date avant les lignes.',
            },
            shot: { motif: 'form', caption: { ar: 'بيانات الرأس', en: 'Header fields', fr: 'En-tête' }, highlight: { ar: 'المخزن يحدد الخصم', en: 'Store drives stock out', fr: 'Dépôt = sortie stock' } },
          },
        ],
      },
      {
        title: { ar: 'الخطوة ب: البنود', en: 'Step B: Lines', fr: 'Étape B' },
        steps: [
          {
            title: { ar: 'بحث صنف أو باركود', en: 'Item search / barcode', fr: 'Article / code-barres' },
            body: {
              ar: 'أضف الأصناف بالبحث أو المسح. راجع الكمية والسعر والخصم والضريبة لكل سطر. احذف السطر الخطأ فوراً قبل الحفظ.',
              en: 'Add items by search or scan. Check qty, price, discount, tax per line. Remove wrong lines before saving.',
              fr: 'Ajoutez articles, vérifiez qté/prix/remise/taxe.',
            },
            shot: { src: '/help/invoices/lines.svg', motif: 'list', caption: { ar: 'بنود الفاتورة', en: 'Invoice lines', fr: 'Lignes' } },
          },
        ],
      },
      {
        title: { ar: 'الخطوة ج: الدفع', en: 'Step C: Payment', fr: 'Étape C' },
        steps: [
          {
            title: { ar: 'طريقة التحصيل', en: 'Collection method', fr: 'Encaissement' },
            body: {
              ar: 'نقدي / شبكة / آجل / متعدد. للأجل يتأثر رصيد العميل. بعد الحفظ اطبع الفاتورة إن لزم.',
              en: 'Cash / card / credit / split. Credit affects customer balance. Print after save if needed.',
              fr: 'Espèces / carte / crédit. Impression après enregistrement.',
            },
            shot: { src: '/help/invoices/payment.svg', motif: 'payment', caption: { ar: 'التحصيل', en: 'Payment', fr: 'Paiement' } },
          },
        ],
      },
    ],
    tips: [
      { ar: 'إن ظهر نقص مخزون: غيّر المخزن أو عدّل الكمية أو أضف شراء أولاً.', en: 'If stock shortage: change store, reduce qty, or purchase first.', fr: 'Stock insuffisant : autre dépôt, qté, ou achat.' },
      { ar: 'نقطة البيع (POS) أسرع للبيع المتكرر بنفس الخطوات المختصرة.', en: 'POS is faster for repetitive retail selling.', fr: 'La caisse accélère la vente répétitive.' },
    ],
  },
  {
    match: ['/purchase-invoices/new', '/purchase-invoices/:id/edit'],
    title: { ar: 'شرح تفصيلي: فاتورة الشراء', en: 'Detailed guide: Purchase invoice', fr: 'Guide: Facture achat' },
    summary: {
      ar: 'تسجيل شراء صحيح يزيد المخزون ويرفع رصيد المورد ويضبط التكلفة.',
      en: 'A correct purchase increases stock, vendor balance, and cost accuracy.',
      fr: 'Achat correct : stock ↑, solde fournisseur ↑, coût juste.',
    },
    emoji: '🛒',
    accent: '#047857',
    gallery: [{ src: '/help/purchase-invoices/new.svg', motif: 'form', caption: { ar: 'فاتورة شراء', en: 'Purchase invoice', fr: 'Achat' } }],
    sections: [
      {
        title: { ar: 'المورد والاستلام', en: 'Vendor & receiving', fr: 'Fournisseur et réception' },
        steps: [
          {
            title: { ar: 'بيانات الشراء', en: 'Purchase header', fr: 'En-tête' },
            body: {
              ar: 'اختر المورد، مخزن الاستلام، التاريخ، ورقم فاتورة المورد إن وُجد للمطابقة البنكية/الضريبية لاحقاً.',
              en: 'Select vendor, receiving store, date, and vendor invoice number for later tax/bank matching.',
              fr: 'Fournisseur, dépôt, date, n° facture fournisseur.',
            },
            shot: { motif: 'form', caption: { ar: 'رأس الشراء', en: 'Purchase header', fr: 'En-tête' } },
          },
          {
            title: { ar: 'الكميات والتكلفة', en: 'Qty & cost', fr: 'Qté et coût' },
            body: {
              ar: 'أدخل كل صنف بكمية وسعر تكلفة دقيق. التكلفة الخاطئة تفسد متوسط المخزون وتقارير الربحية.',
              en: 'Enter each item with accurate qty and unit cost. Wrong cost corrupts average stock and margin reports.',
              fr: 'Qté et coût unitaires exacts — impact stock moyen.',
            },
            shot: { src: '/help/purchase-invoices/lines.svg', motif: 'list', caption: { ar: 'بنود الشراء', en: 'Purchase lines', fr: 'Lignes' }, highlight: { ar: 'التكلفة مهمة', en: 'Cost matters', fr: 'Le coût compte' } },
          },
          {
            title: { ar: 'الحفظ', en: 'Save', fr: 'Enregistrer' },
            body: {
              ar: 'بعد المراجعة احفظ. سيُحدَّث المخزون وحساب المورد. يمكنك الطباعة أو فتح مرتجع لاحقاً عند الحاجة.',
              en: 'After review, save — stock and vendor account update. Print or create a return later if needed.',
              fr: 'Enregistrez — stock et compte fournisseur mis à jour.',
            },
            shot: { motif: 'approve', caption: { ar: 'ترحيل الشراء', en: 'Post purchase', fr: 'Valider' } },
          },
        ],
      },
    ],
    tips: [
      { ar: 'اربط بأمر شراء عند توفره لتقليل الأخطاء.', en: 'Link a purchase order when available to reduce typos.', fr: 'Liez une commande d’achat si possible.' },
    ],
  },
  {
    match: ['/customers', '/customers*'],
    title: { ar: 'شرح تفصيلي: العملاء', en: 'Detailed guide: Customers', fr: 'Guide: Clients' },
    summary: {
      ar: 'إدارة بطاقة العميل، الرصيد، وكشف الحساب، وربطها بالفواتير والتحصيل.',
      en: 'Manage customer cards, balances, statements, and links to invoices/collections.',
      fr: 'Fiches clients, soldes, relevés et factures.',
    },
    emoji: '👥',
    accent: '#0891b2',
    gallery: [{ src: '/help/customers/list.svg', motif: 'list', caption: { ar: 'قائمة العملاء', en: 'Customer list', fr: 'Liste' } }],
    sections: [
      {
        title: { ar: 'إضافة عميل', en: 'Add customer', fr: 'Ajouter' },
        steps: [
          {
            title: { ar: 'البيانات الأساسية', en: 'Basic data', fr: 'Base' },
            body: {
              ar: 'الاسم مطلوب. أضف الهاتف والعنوان والرقم الضريبي إن وُجد. يمكن ربط حساب محاسبي افتراضي للترحيل.',
              en: 'Name is required. Add phone, address, tax id if any. Optionally link a default posting account.',
              fr: 'Nom requis. Téléphone, adresse, n° fiscal si besoin.',
            },
            shot: { src: '/help/customers/form.svg', motif: 'form', caption: { ar: 'نموذج العميل', en: 'Customer form', fr: 'Formulaire' } },
          },
          {
            title: { ar: 'الرصيد والمتابعة', en: 'Balance follow-up', fr: 'Suivi solde' },
            body: {
              ar: 'من القائمة راقب الرصيد. افتح كشف الحساب لترى الفواتير والسندات. أنشئ فاتورة أو سند قبض مباشرة عند الحاجة.',
              en: 'Watch balance in the list. Open the statement for invoices and vouchers. Create invoice or receipt from here when needed.',
              fr: 'Solde, relevé, facture ou reçu depuis la fiche.',
            },
            shot: { motif: 'payment', caption: { ar: 'الرصيد والتحصيل', en: 'Balance & collection', fr: 'Solde' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/items', '/items*', '/items-directory'],
    title: { ar: 'شرح تفصيلي: الأصناف', en: 'Detailed guide: Items', fr: 'Guide: Articles' },
    summary: {
      ar: 'تعريف الصنف ووحداته وأسعاره هو أساس الفواتير والجرد والتقارير.',
      en: 'Item setup (units & prices) is the foundation for invoices, stock, and reports.',
      fr: 'Articles, unités et prix — base factures/stock.',
    },
    emoji: '📦',
    accent: '#0369a1',
    gallery: [{ src: '/help/items/list.svg', motif: 'list', caption: { ar: 'دليل الأصناف', en: 'Items list', fr: 'Catalogue' } }],
    sections: [
      {
        title: { ar: 'بطاقة الصنف', en: 'Item card', fr: 'Fiche article' },
        steps: [
          {
            title: { ar: 'الاسم والباركود والوحدة', en: 'Name, barcode, unit', fr: 'Nom, code, unité' },
            body: {
              ar: 'أدخل الاسم والفئة والباركود والوحدة الأساسية. أضف وحدات بيع/شراء إضافية إن بعت بالصندوق واشتريت بالحبة مثلاً.',
              en: 'Enter name, category, barcode, base unit. Add extra sale/purchase units if you sell/buy in different packs.',
              fr: 'Nom, catégorie, code-barres, unité de base + unités secondaires.',
            },
            shot: { src: '/help/items/form.svg', motif: 'form', caption: { ar: 'نموذج الصنف', en: 'Item form', fr: 'Formulaire' } },
          },
          {
            title: { ar: 'الأسعار والمخزون', en: 'Prices & stock', fr: 'Prix et stock' },
            body: {
              ar: 'حدد سعر البيع والتكلفة. راقب الرصيد حسب المخزن. لتعديل أسعار كثيرة استخدم شاشة تعديل الأسعار الجماعي.',
              en: 'Set sale price and cost. Watch stock per store. Use bulk prices editor for many items at once.',
              fr: 'Prix vente/coût. Stock par dépôt. Éditeur de prix en masse.',
            },
            shot: { motif: 'dashboard', caption: { ar: 'أسعار وأرصدة', en: 'Prices & balances', fr: 'Prix/stocks' } },
          },
        ],
      },
    ],
    tips: [
      { ar: 'لا تحذف صنفاً مستخدماً في فواتير — عطّله بدلاً من ذلك.', en: 'Do not delete items used in invoices — deactivate instead.', fr: 'Ne supprimez pas un article utilisé — désactivez.' },
    ],
  },
  {
    match: ['/journal', '/journal*'],
    title: { ar: 'شرح تفصيلي: قيود اليومية', en: 'Detailed guide: Journal', fr: 'Guide: Écritures' },
    summary: {
      ar: 'قيود يدوية متوازنة مدين/دائن مع بيان وتاريخ ومركز تكلفة اختياري.',
      en: 'Manual balanced debit/credit entries with narration, date, and optional cost center.',
      fr: 'Écritures équilibrées débit/crédit.',
    },
    emoji: '📒',
    accent: '#1d4ed8',
    sections: [
      {
        title: { ar: 'بناء القيد', en: 'Build the entry', fr: 'Construire' },
        steps: [
          {
            title: { ar: 'الرأس', en: 'Header', fr: 'En-tête' },
            body: {
              ar: 'حدد التاريخ والبيان والمرجع. أضف أسطر مدين ودائن للحسابات الصحيحة من الدليل المحاسبي.',
              en: 'Set date, narration, reference. Add debit and credit lines from the chart of accounts.',
              fr: 'Date, libellé, référence, lignes débit/crédit.',
            },
            shot: { motif: 'form', caption: { ar: 'نموذج القيد', en: 'Journal form', fr: 'Formulaire' } },
          },
          {
            title: { ar: 'التوازن', en: 'Balance check', fr: 'Équilibre' },
            body: {
              ar: 'قبل الحفظ تأكد أن مجموع المدين = مجموع الدائن. راجع مراكز التكلفة إن كانت مفعّلة.',
              en: 'Before save ensure total debit = total credit. Review cost centers if enabled.',
              fr: 'Débit total = crédit total avant enregistrement.',
            },
            shot: { motif: 'approve', caption: { ar: 'قيد متوازن', en: 'Balanced entry', fr: 'Équilibré' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/vouchers', '/vouchers*'],
    title: { ar: 'شرح تفصيلي: السندات', en: 'Detailed guide: Vouchers', fr: 'Guide: Pièces' },
    summary: {
      ar: 'سند قبض أو صرف يحدّث الصندوق/البنك ورصيد العميل أو المورد.',
      en: 'Receipt/payment vouchers update cash/bank and customer or vendor balance.',
      fr: 'Encaissement/décaissement — caisse/banque et tiers.',
    },
    emoji: '💵',
    accent: '#15803d',
    sections: [
      {
        title: { ar: 'إنشاء سند', en: 'Create voucher', fr: 'Créer' },
        steps: [
          {
            title: { ar: 'النوع والطرف', en: 'Type & party', fr: 'Type et tiers' },
            body: {
              ar: 'اختر قبض أو صرف، ثم العميل/المورد/الحساب، والمبلغ، وطريقة الدفع، والتاريخ.',
              en: 'Choose receipt or payment, then party, amount, method, and date.',
              fr: 'Encaissement ou paiement, tiers, montant, mode, date.',
            },
            shot: { motif: 'payment', caption: { ar: 'شاشة السند', en: 'Voucher screen', fr: 'Pièce' } },
          },
          {
            title: { ar: 'الربط بالفواتير', en: 'Link invoices', fr: 'Lier factures' },
            body: {
              ar: 'إن وُجدت فواتير مفتوحة يمكن تخصيص السند عليها لتقليل الرصيد المتبقي بدقة.',
              en: 'If open invoices exist, allocate the voucher to reduce remaining balances accurately.',
              fr: 'Imputez sur les factures ouvertes si possible.',
            },
            shot: { motif: 'list', caption: { ar: 'تخصيص الفواتير', en: 'Invoice allocation', fr: 'Imputation' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/pos'],
    title: { ar: 'شرح تفصيلي: نقطة البيع', en: 'Detailed guide: POS', fr: 'Guide: Caisse' },
    summary: {
      ar: 'بيع سريع بالباركود والطاولات (إن وُجدت) مع اختصارات لوحة المفاتيح والتحصيل الفوري.',
      en: 'Fast selling with barcode/tables, keyboard shortcuts, and instant payment.',
      fr: 'Vente rapide code-barres, raccourcis et paiement.',
    },
    emoji: '🖥️',
    accent: '#c2410c',
    sections: [
      {
        title: { ar: 'دورة البيع', en: 'Sale cycle', fr: 'Cycle' },
        steps: [
          {
            title: { ar: 'أضف الأصناف', en: 'Add items', fr: 'Articles' },
            body: {
              ar: 'امسح الباركود أو اختر من القائمة. عدّل الكمية من لوحة المفاتيح. راقب الإجمالي والضريبة مباشرة.',
              en: 'Scan barcode or pick from menu. Adjust qty via keyboard. Watch total and tax live.',
              fr: 'Scannez ou choisissez, ajustez la qté, total live.',
            },
            shot: { motif: 'list', caption: { ar: 'سلة البيع', en: 'Sale basket', fr: 'Panier' } },
          },
          {
            title: { ar: 'ادفع واطبع', en: 'Pay & print', fr: 'Payer et imprimer' },
            body: {
              ar: 'اختر طريقة الدفع وأكمل. تفتح الطباعة تلقائياً حسب إعداد الطابعة.',
              en: 'Choose payment method and complete. Printing may start automatically per printer settings.',
              fr: 'Mode de paiement puis impression auto selon config.',
            },
            shot: { motif: 'payment', caption: { ar: 'الدفع', en: 'Checkout', fr: 'Encaissement' } },
          },
        ],
      },
    ],
    tips: [
      { ar: 'تعرّف على اختصارات F1 وغيرها من زر المساعدة داخل POS.', en: 'Learn F1 and other shortcuts from the in-POS help.', fr: 'Raccourcis F1 dans l’aide caisse.' },
    ],
  },
  {
    match: ['/reports', '/reports*'],
    title: { ar: 'شرح تفصيلي: التقارير', en: 'Detailed guide: Reports', fr: 'Guide: Rapports' },
    summary: {
      ar: 'من فهرس التقارير: اختر التقرير → فلتر → عرض → طباعة/تصدير.',
      en: 'From the report index: pick → filter → view → print/export.',
      fr: 'Index → filtres → affichage → export.',
    },
    emoji: '📈',
    accent: '#7c2d12',
    sections: [
      {
        title: { ar: 'تشغيل أي تقرير', en: 'Run any report', fr: 'Lancer' },
        steps: [
          {
            title: { ar: 'الاختيار', en: 'Choose', fr: 'Choisir' },
            body: {
              ar: 'من فهرس التقارير افتح التقرير (مبيعات تفصيلية، مخزون، محاسبة، بلدية…).',
              en: 'Open the report you need (detailed sales, stock, accounting, municipality…).',
              fr: 'Ouvrez le rapport souhaité.',
            },
            shot: { motif: 'dashboard', caption: { ar: 'فهرس التقارير', en: 'Reports index', fr: 'Index' } },
          },
          {
            title: { ar: 'الفلاتر والتصدير', en: 'Filters & export', fr: 'Filtres et export' },
            body: {
              ar: 'حدد الفترة والفرع/المخزن/الحساب ثم اعرض. صدّر Excel للتحليل أو اطبع للاجتماعات.',
              en: 'Set period and branch/store/account, then view. Export Excel for analysis or print for meetings.',
              fr: 'Période et filtres, affichage, Excel ou impression.',
            },
            shot: { motif: 'print', caption: { ar: 'تصدير', en: 'Export', fr: 'Export' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/production/boms', '/production/boms*', '/production/orders', '/production/orders*'],
    title: { ar: 'شرح تفصيلي: الإنتاج', en: 'Detailed guide: Production', fr: 'Guide: Production' },
    summary: {
      ar: 'وصفة مفعّلة → أمر إنتاج → سحب مواد (مع بدائل) → استلام صالح/تالف → إكمال مع إرجاع الفائض.',
      en: 'Active BOM → order → issue (with alternatives) → receive good/scrap → complete with unused return.',
      fr: 'BOM active → OF → sortie (alternatives) → entrée/rebut → clôture avec retour.',
    },
    emoji: '🏭',
    accent: '#0e7490',
    sections: [
      {
        title: { ar: 'المسار الكامل للمصنع', en: 'Full factory path', fr: 'Parcours usine' },
        steps: [
          {
            title: { ar: '١) الوصفة والتفعيل', en: '1) BOM & activate', fr: '1) BOM' },
            body: {
              ar: 'نظام الإنتاج → وصفات. أنشئ الوصفة (منتج + مواد + فاقد + بدائل)، احفظ مسودة، ثم «تفعيل مباشر» أو مسار الاعتماد. الأوامر تستخدم النشطة فقط.',
              en: 'Production → BOMs. Create recipe (product + materials + waste + alternatives), save Draft, then quick-activate or approval path. Orders use Active only.',
              fr: 'Créer BOM, brouillon, activer. OF = BOM active uniquement.',
            },
            shot: { motif: 'form', caption: { ar: 'وصفة الإنتاج', en: 'BOM form', fr: 'BOM' } },
          },
          {
            title: { ar: '٢) الأمر والسحب', en: '2) Order & issue', fr: '2) OF & sortie' },
            body: {
              ar: 'أوامر الإنتاج → جديد من وصفة نشطة. من القائمة ↓ سحب المواد بعد فحص الرصيد؛ عند النقص تُجرَّب بدائل الوصفة تلقائياً.',
              en: 'Orders → new from Active BOM. List ↓ issue after stock check; on shortage BOM alternatives are tried automatically.',
              fr: 'Nouvel OF, contrôle stock, sortie; alternatives auto si manque.',
            },
            shot: { motif: 'list', caption: { ar: 'سحب المواد', en: 'Issue materials', fr: 'Sortie' } },
          },
          {
            title: { ar: '٣) الاستلام والإغلاق', en: '3) Receive & close', fr: '3) Réception' },
            body: {
              ar: '↑ استلام: كمية صالحة للمخزن + تالف مسجّل. عند اكتمال المخطط أو ✓ إكمال: يُغلق الأمر ويُرجَع غير المستخدم من المواد. ⊗ إلغاء يعكس الحركات.',
              en: '↑ Receive: good qty to stock + recorded scrap. On full plan or ✓ complete: order closes and unused materials return. ⊗ Cancel reverses movements.',
              fr: 'Entrée PF + rebut, clôture, retour matières; annulation inverse.',
            },
            shot: { motif: 'approve', caption: { ar: 'استلام وإكمال', en: 'Receive & complete', fr: 'Clôture' } },
          },
        ],
      },
      {
        title: { ar: 'نصائح تشغيل', en: 'Operating tips', fr: 'Astuces' },
        steps: [
          {
            title: { ar: 'الوحدات والمخزون', en: 'Units & stock', fr: 'Unités' },
            body: {
              ar: 'استخدم وحدات الصنف المرتبطة بالمخزون. الكمية في الأمر بنفس وحدة الوصفة (مع الفاقد) حتى لا يختلف السحب عن الرصيد.',
              en: 'Use inventory-linked item units. Order qty stays in the BOM unit (with waste) so issue matches stock.',
              fr: 'Unités stock; qté OF = unité BOM + pertes.',
            },
            shot: { motif: 'search', caption: { ar: 'وحدات ومخزون', en: 'Units & stock', fr: 'Stock' } },
          },
          {
            title: { ar: 'الإصدارات', en: 'Versions', fr: 'Versions' },
            body: {
              ar: 'لا تعدّل وصفة نشطة مستخدمة؛ أنشئ إصداراً جديداً، عدّله كمسودة، ثم فعّله. الإصدار السابق يُؤرشف تلقائياً.',
              en: 'Do not edit an Active BOM in use; create a new version, edit as Draft, then activate. Previous Active is archived.',
              fr: 'Nouvelle version en brouillon puis activation; ancienne archivée.',
            },
            shot: { motif: 'form', caption: { ar: 'إصدار جديد', en: 'New version', fr: 'Version' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/hr/employees', '/hr/employees*', '/hr*'],
    title: { ar: 'شرح تفصيلي: الموارد البشرية', en: 'Detailed guide: HR', fr: 'Guide: RH' },
    summary: {
      ar: 'الموظف → القسم/الوظيفة → العقد → الحضور والجداول → الإجازات والسلف.',
      en: 'Employee → department/job → contract → attendance/schedules → leave & advances.',
      fr: 'Employé → service/poste → contrat → présence → congés/avances.',
    },
    emoji: '👔',
    accent: '#0f766e',
    sections: [
      {
        title: { ar: 'التسلسل الصحيح', en: 'Correct sequence', fr: 'Séquence' },
        steps: [
          {
            title: { ar: 'الملف التعاقدي', en: 'Employee & contract', fr: 'Dossier' },
            body: {
              ar: 'أنشئ الموظف والقسم والوظيفة ثم العقد. بدون عقد/جدول قد تفشل سياسات الحضور.',
              en: 'Create employee, department, job profile, then contract. Without contract/schedule attendance policies may fail.',
              fr: 'Employé, service, poste, puis contrat.',
            },
            shot: { motif: 'form', caption: { ar: 'ملف الموظف', en: 'Employee file', fr: 'Fiche' } },
          },
          {
            title: { ar: 'الحضور والطلبات', en: 'Attendance & requests', fr: 'Présence' },
            body: {
              ar: 'اربط جدول العمل وسياسة الحضور. سجّل الإجازات والسلف وقيود الوقت من الشاشات المخصصة.',
              en: 'Attach work schedule and attendance policy. Log leave, advances, and time entries from their screens.',
              fr: 'Planning, politique, congés, avances, temps.',
            },
            shot: { motif: 'list', caption: { ar: 'الحضور والطلبات', en: 'Attendance & requests', fr: 'Présence' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/settings', '/settings*', '/users', '/roles', '/permissions', '/permissions*'],
    title: { ar: 'شرح تفصيلي: الإعدادات والصلاحيات', en: 'Detailed guide: Settings & access', fr: 'Guide: Paramètres' },
    summary: {
      ar: 'ضبط الشركة والطباعة والدفع والمستخدمين والأدوار بأمان.',
      en: 'Configure company, printing, payments, users, and roles safely.',
      fr: 'Société, impression, paiements, utilisateurs et rôles.',
    },
    emoji: '⚙️',
    accent: '#475569',
    sections: [
      {
        title: { ar: 'الأمان أولاً', en: 'Safety first', fr: 'Sécurité' },
        steps: [
          {
            title: { ar: 'بيانات الشركة', en: 'Company profile', fr: 'Société' },
            body: {
              ar: 'الاسم والشعار والضريبة والعام المالي تظهر على المطبوعات — حدّثها قبل الطباعة الرسمية.',
              en: 'Name, logo, tax, and fiscal year appear on printouts — update before official printing.',
              fr: 'Nom, logo, taxe, exercice — avant impression officielle.',
            },
            shot: { motif: 'settings', caption: { ar: 'إعدادات الشركة', en: 'Company settings', fr: 'Société' } },
          },
          {
            title: { ar: 'الأدوار', en: 'Roles', fr: 'Rôles' },
            body: {
              ar: 'أنشئ دوراً لكل وظيفة (كاشير، محاسب…) واملأ الصلاحيات اللازمة فقط، ثم اربط المستخدمين.',
              en: 'Create a role per job (cashier, accountant…), grant only needed permissions, then assign users.',
              fr: 'Un rôle par métier, droits minimaux, puis utilisateurs.',
            },
            shot: { motif: 'list', caption: { ar: 'المستخدمون والأدوار', en: 'Users & roles', fr: 'Rôles' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/municipality', '/municipality*', '/reports/municipality*'],
    title: { ar: 'شرح تفصيلي: البلدية', en: 'Detailed guide: Municipality', fr: 'Guide: Municipalité' },
    summary: {
      ar: 'خدمات، شكاوى، تراخيص، مشاريع، تحصيل، ووثائق رسمية — مع تقارير متابعة.',
      en: 'Services, complaints, licenses, projects, collections, official documents — plus tracking reports.',
      fr: 'Services, plaintes, licences, projets, recettes, documents + rapports.',
    },
    emoji: '🏛️',
    accent: '#0f766e',
    sections: [
      {
        title: { ar: 'منهج العمل', en: 'Working method', fr: 'Méthode' },
        steps: [
          {
            title: { ar: 'الأنواع أولاً', en: 'Types first', fr: 'Types d’abord' },
            body: {
              ar: 'قبل تسجيل طلب حي: عرّف أنواع الخدمات/الشكاوى/التراخيص/المشاريع. الأنواع توحّد التقارير لاحقاً.',
              en: 'Before live requests: define service/complaint/license/project types. Types keep reports consistent.',
              fr: 'Définissez les types avant les demandes réelles.',
            },
            shot: { motif: 'settings', caption: { ar: 'شاشات الأنواع', en: 'Type screens', fr: 'Types' } },
          },
          {
            title: { ar: 'التنفيذ والمتابعة', en: 'Execute & track', fr: 'Exécuter' },
            body: {
              ar: 'سجّل الطلب أو الرخصة أو المشروع، حدّث الحالة، اربط التحصيل أو المصروف، ثم راجع تقرير البلدية.',
              en: 'Record the request/license/project, update status, link collection or expense, then review municipality reports.',
              fr: 'Saisie, statut, recettes/dépenses, puis rapports.',
            },
            shot: { motif: 'list', caption: { ar: 'سجلات البلدية', en: 'Municipality records', fr: 'Registres' } },
          },
        ],
      },
    ],
  },
]
